import { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { Anchor } from '@twilio-paste/core/anchor';
import { Box } from '@twilio-paste/core/box';
import { Button } from '@twilio-paste/core/button';
import { Menu, MenuBadge, useMenuState } from '@twilio-paste/core/menu';
import { Text } from '@twilio-paste/core/text';
import { ProcessWarningIcon } from '@twilio-paste/icons/esm/ProcessWarningIcon';
import CompositeCallout from '@twiliointernal/services-common/components/compositeCallout';

import { oscLayoutCopy } from '@twiliointernal/e2-commons-shared-definitions';
import { types } from '../definitions';
import styles from '../layout.module.css';

const ErrorMenu = ({
  type,
  redirectUrl,
}: types.ErrorMenuProps): ReactElement => {
  const navigate = useNavigate();

  const errorMenuState = useMenuState({ gutter: 12 });
  const hasSupportPlansError = type === types.ErrorMenuType.supportPlan;
  const errorCopy = hasSupportPlansError
    ? oscLayoutCopy.topbar.supportPlan
    : oscLayoutCopy.topbar.systemStatus;

  const renderCta = (): ReactElement =>
    hasSupportPlansError ? (
      <Button
        variant="primary"
        onClick={(): void => {
          navigate(redirectUrl);
          errorMenuState.hide();
        }}
      >
        {errorCopy.error.ctaValue}
      </Button>
    ) : (
      <Anchor href={redirectUrl} showExternal>
        {errorCopy.error.ctaValue}
      </Anchor>
    );

  return (
    <Box as="span" className={styles.topbarActionContainer}>
      <Text
        as="span"
        fontSize="fontSize20"
        fontWeight="fontWeightSemibold"
        color="colorTextWeak"
      >{`${errorCopy.label}:`}</Text>

      <MenuBadge
        {...errorMenuState}
        variant="default"
        i18nButtonLabel={errorCopy.label}
      >
        <ProcessWarningIcon
          color="colorTextIconWarning"
          decorative
          size="sizeIcon10"
        />
        {errorCopy.error.menuValue}
      </MenuBadge>

      <Menu
        {...errorMenuState}
        aria-label={oscLayoutCopy.topbar.supportPlan.detailsLabel}
      >
        <Box as="div" className={styles.topbarMenuDetailsContainer}>
          <CompositeCallout
            heading={errorCopy.error.heading}
            description={errorCopy.error.description}
            variant="warning"
          />

          {renderCta()}
        </Box>
      </Menu>
    </Box>
  );
};

export default ErrorMenu;
